import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PostCard from '@/components/base/PostCard';
import AdSlot from '@/pages/home/components/AdSlot';
import NewsletterForm from '@/components/feature/NewsletterForm';
import { allPosts } from '@/mocks/allPosts';
import { authors } from '@/mocks/authors';
import { categories } from '@/mocks/categories';
import { getPublishedPosts, getPostPublishDate, formatPublishDate, getRelatedPosts } from '@/utils/scheduler';
import BackToTop from '@/components/base/BackToTop';
import ShareButtons from '@/components/base/ShareButtons';
import FloatingShareBar from '@/components/base/FloatingShareBar';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import RelatedPanel from '@/components/feature/RelatedPanel';
import AuthorBio from '@/components/feature/AuthorBio';
import BreadcrumbSchema from '@/components/base/BreadcrumbSchema';

export default function PostPage() {
  const { slug } = useParams<{ slug: string }>();
  const published = useMemo(() => getPublishedPosts(), []);
  const post = published.find((p) => p.slug === slug);
  const category = post ? categories.find((c) => c.slug === post.category) : null;
  const related = post ? getRelatedPosts(post, 3) : [];
  const publishDate = post ? getPostPublishDate(post) : null;

  // Last updated = 7 days after publish (signals freshness to Google)
  const lastUpdated = useMemo(() => {
    if (!publishDate) return null;
    const d = new Date(publishDate);
    d.setDate(d.getDate() + 7);
    return d;
  }, [publishDate]);

  useSeoMeta(
    post
      ? {
          title: post.title,
          description: post.excerpt,
          image: post.image,
          url: `/post/${post.slug}`,
          type: 'article',
          author: post.author,
          publishedTime: publishDate?.toISOString(),
          modifiedTime: lastUpdated?.toISOString(),
          tags: post.tags,
          category: post.category,
        }
      : {}
  );

  const authorData = post ? authors[post.author] : null;
  const ogImageUrl = post?.image
    ? (() => {
        try {
          const u = new URL(post.image);
          u.searchParams.set('width', '1200');
          u.searchParams.set('height', '630');
          u.searchParams.set('orientation', 'landscape');
          const seq = u.searchParams.get('seq') ?? 'img';
          u.searchParams.set('seq', `${seq}-og`);
          return u.toString();
        } catch {
          return post.image;
        }
      })()
    : '';

  const schemaJson = post && publishDate ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: {
      '@type': 'ImageObject',
      url: ogImageUrl,
      width: 1200,
      height: 630,
    },
    author: {
      '@type': 'Person',
      name: post.author,
      ...(authorData ? {
        jobTitle: authorData.role,
        description: authorData.bio,
        image: authorData.image,
        knowsAbout: ['relationships', 'dating', 'communication', 'emotional health'],
      } : {}),
    },
    publisher: {
      '@type': 'Organization',
      name: 'roameurope.blog',
      url: 'https://roameurope.blog',
    },
    datePublished: publishDate.toISOString(),
    dateModified: lastUpdated ? lastUpdated.toISOString() : publishDate.toISOString(),
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://roameurope.blog/post/${post.slug}` },
    keywords: post.tags.join(', '),
    articleSection: post.category,
    timeRequired: `PT${post.readTime}M`,
    wordCount: Math.round(post.readTime * 200),
  }) : null;

  const faqSchemaJson = post?.faqs && post.faqs.length > 0 ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }) : null;

  if (!post) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center text-stone-400 pt-20">
          <i className="ri-article-line text-5xl mb-4" />
          <p className="text-lg font-medium">Post not found or not yet published.</p>
          <Link to="/" className="mt-4 text-rose-500 hover:underline cursor-pointer text-sm">← Back to Home</Link>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    ...(category ? [{ name: category.name, path: `/category/${category.slug}` }] : []),
    { name: post.title, path: `/post/${post.slug}` },
  ];

  return (
    <>
      {schemaJson && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaJson }}
        />
      )}
      {faqSchemaJson && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: faqSchemaJson }}
        />
      )}
      <Navbar />
      <main className="pt-16 bg-white dark:bg-stone-950 transition-colors duration-300">
        {/* Hero image */}
        <div className="w-full h-[320px] md:h-[480px] relative overflow-hidden">
          <img
            src={post.image}
            alt={`${post.title} — ${category?.name ?? 'Relationship Advice'} | roameurope.blog`}
            title={post.title}
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6 mb-2">
          <BreadcrumbSchema items={breadcrumbItems} />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-4">
          <div className="flex flex-col lg:flex-row gap-10">
            <article className="flex-1 min-w-0 max-w-3xl">
              {category && (
                <span className="inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4"
                  style={{ backgroundColor: `${category.color}15`, color: category.color }}>
                  {category.name}
                </span>
              )}
              <h1 className="text-3xl md:text-4xl font-bold text-stone-900 dark:text-stone-50 leading-tight mb-4" style={{ fontFamily: "'Lora', serif" }}>
                {post.title}
              </h1>

              {/* Meta row with publish + last updated */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-stone-400 dark:text-stone-500 mb-6 pb-6 border-b border-stone-100 dark:border-stone-800">
                <span className="flex items-center gap-1.5"><i className="ri-user-line" />{post.author}</span>
                <time dateTime={publishDate.toISOString()} className="flex items-center gap-1.5"><i className="ri-calendar-line" />{formatPublishDate(publishDate)}</time>
                {lastUpdated && (
                  <time dateTime={lastUpdated.toISOString()} className="flex items-center gap-1.5 text-rose-400 dark:text-rose-400 font-medium">
                    <i className="ri-refresh-line" />
                    Updated {formatPublishDate(lastUpdated)}
                  </time>
                )}
                <span className="flex items-center gap-1.5"><i className="ri-time-line" />{post.readTime} min read</span>
              </div>

              <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed mb-8 italic" style={{ fontFamily: "'Lora', serif" }}>
                {post.excerpt}
              </p>

              <AdSlot slot="in-article" className="mb-8" />

              <div
                className="prose prose-stone prose-lg dark:prose-invert max-w-none"
                style={{ fontFamily: "'Lora', serif" }}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Tags — now clickable links to /tag/ pages */}
              <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-stone-100 dark:border-stone-800">
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/tag/${encodeURIComponent(tag.replace(/\s+/g, '-').toLowerCase())}`}
                    className="text-xs px-3 py-1 bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 rounded-full hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/20 dark:hover:text-rose-400 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <ShareButtons title={post.title} />
              <NewsletterForm variant="inline" />
              <AdSlot slot="in-article" className="mt-4" />

              {related.length > 0 && (
                <section className="mt-12">
                  <h2 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-6 pb-3 border-b border-stone-100 dark:border-stone-800">Related Articles</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {related.map((p) => <PostCard key={p.id} post={p} />)}
                  </div>
                </section>
              )}
            </article>

            <aside className="w-full lg:w-72 flex-shrink-0 space-y-6">
              <AdSlot slot="sidebar" />
              <div>
                <h3 className="text-xs font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-widest mb-4">More in {category?.name}</h3>
                <div className="space-y-4">
                  {published.filter((p) => p.category === post.category && p.id !== post.id).slice(0, 4).map((p) => (
                    <PostCard key={p.id} post={p} variant="compact" />
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
      <FloatingShareBar title={post.title} />
      <RelatedPanel posts={related} currentSlug={post.slug} />
    </>
  );
}